import React, { FunctionComponent, useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Button, Icon, Responsive, isDesktop, isLargeDesktop, isMobileDevice, isTabletDevice } from 'rs-emd-ui-atoms';
import { encodeURIComponent } from 'window-or-global';
import { Magnifier, MediaModal, PositionIndicator } from '../..';
import { ILabel } from '../../../../models/common/label.model';
import { IPDPMediaItem, MediaType } from '../../../../models/pdp/pdp-media-item.model';
import { IPDPMedia, getThumbnail, isPDPMediaItem } from '../../../../models/pdp/pdp-media.model';
import { IPDPPCBDetails } from '../../../../models/pdp/pdp-pcb-details.model';
import { IPDPStickersItem } from '../../../../models/pdp/pdp-stickers.model';
import { IProductService } from '../../../../services/product/product-service';
import pcb3d from './../../../../images/pcb-3d.svg';
import snapeda from './../../../../images/snapeda.svg';
import styles from './media-carousel-component.module.scss';

type MediaCarouselProps = {
  media: IPDPMedia;
  sticker?: IPDPStickersItem;
  labels?: ILabel[] | null;
  productService?: IProductService;
};

type State = {
  areDesktopChevronsVisible: boolean;
  isChevronUpEnabled: boolean;
  isChevronDownEnabled: boolean;
  selectedMediaIndex: number;
  isMediaModalOpen: boolean;
  isPCBModalOpen: boolean;
  currentMediaMPN?: string;
  currentMediaBrand?: string;
  showPCBBtn: boolean;
  isPCBAvailable?: boolean;
};

type Action =
  | { type: 'resetState' | 'toggleMediaModal' }
  | { type: 'enableChevrons'; isChevronUpEnabled: boolean; isChevronDownEnabled: boolean }
  | { type: 'selectThumbnail'; index: number }
  | { type: 'setCurrentMedia'; currentMediaMPN?: string; currentMediaBrand?: string }
  | { type: 'togglePCBBtnVisibility'; isVisible: boolean }
  | { type: 'setPCBAvailibility'; isAvailable: boolean };

export const MediaCarousel: FunctionComponent<MediaCarouselProps> = (props: MediaCarouselProps) => {
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const mediaWrapperRef = useRef<HTMLDivElement>(null);
  const initialState = {
    areDesktopChevronsVisible: true,
    isChevronUpEnabled: false,
    isChevronDownEnabled: true,
    selectedMediaIndex: 0,
    isMediaModalOpen: false,
    isPCBModalOpen: false,
    showPCBBtn: false,
    isPCBAvailable: undefined,
  };

  useLayoutEffect(() => {
    resetState();
  }, []);

  useEffect(() => {
    //TODO: optimization - check only if 4/5 and if desktop/lg-desktop?
    window.addEventListener('resize', resetState);

    return () => {
      window.removeEventListener('resize', resetState);
    };
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);

  //check PCB availibility
  useEffect(() => {
    if (
      state.isPCBAvailable === undefined &&
      props.media.media.some((m) => !isPDPMediaItem(m)) &&
      state.currentMediaBrand &&
      state.currentMediaMPN
    )
      props.productService
        ?.checkPCBAvailability({ manufacturer: state.currentMediaBrand, partNumber: state.currentMediaMPN })
        .then((res) => {
          if (res.data) {
            dispatch({ type: 'setPCBAvailibility', isAvailable: res.data.isPCBAvailable });
          }
        });
  }, [props.media.media, props.productService, state.currentMediaBrand, state.currentMediaMPN, state.isPCBAvailable]);

  // add pcb script
  useEffect(() => {
    if (
      state.isPCBAvailable &&
      state.currentMediaBrand &&
      state.currentMediaMPN &&
      state.showPCBBtn &&
      document.getElementById('snapeda-id') === null &&
      document.getElementById('pcb-button') !== null
    ) {
      const script = document.createElement('script');

      script.src = 'https://www.snapeda.com/distributor/files/plugins/snapeda/snapeda.min.js?token=2ccb86c928f0ae7f6cd304414a7686';
      script.setAttribute('id', 'snapeda-id');
      script.setAttribute('defer', 'true');
      script.setAttribute('autoload', 'true');
      script.setAttribute('part', `${encodeURIComponent(state.currentMediaMPN)}`);
      script.setAttribute('manufacturer', `${encodeURIComponent(state.currentMediaBrand)}`);
      script.setAttribute('token', '2ccb86c928f0ae7f6cd304414a7686');
      script.setAttribute('container', '#pcb-button');
      script.setAttribute('host', 'https://www.snapeda.com');
      script.setAttribute('lang', 'en');

      document.body.appendChild(script);
    }
  }, [state.currentMediaBrand, state.currentMediaMPN, state.isPCBAvailable, state.showPCBBtn]);

  // remove pcb script
  useEffect(() => {
    if (!state.showPCBBtn && document.getElementById('snapeda-id') !== null) {
      const snapedaScript = document.getElementById('snapeda-id');
      const mixPanelScript = Array.from(document.getElementsByTagName('script')).filter(
        (s) => s.src === 'https://cdn.mxpnl.com/libs/mixpanel-2.2.min.js'
      );

      if (snapedaScript) document.body.removeChild(snapedaScript);
      if (mixPanelScript.length > 1) mixPanelScript[0].remove();
    }
  }, [state.showPCBBtn]);

  const handlers = useSwipeable({
    onSwipedLeft: () => onMobileSwipe(false),
    onSwipedRight: () => onMobileSwipe(true),
    trackMouse: true,
    trackTouch: true,
    preventScrollOnSwipe: true,
  });

  let updatedMedia =
    props.media.media.some((m) => !isPDPMediaItem(m)) && !state.isPCBAvailable
      ? { ...props.media, media: props.media.media.filter((m) => isPDPMediaItem(m)) }
      : props.media;

  return (
    <div className={styles['gallery-container']}>
      {/* desktop & lg-desktop carousel thumbnails */}
      <div>
        <Responsive breakpoints={['desktop', 'lg-desktop']}>
          <div className={styles['carousel']}>
            <Icon
              name='chev-up'
              className={`
        ${styles.chevron} 
        ${styles.up} 
        ${!state.areDesktopChevronsVisible ? styles.hide : ''} 
        ${!state.isChevronUpEnabled ? styles.disabled : ''}`}
              onClick={() => onDesktopChevronClick(true)}></Icon>

            <div className={styles.thumbnails} ref={thumbnailsRef}>
              {props.media &&
                props.media.media.map((m, index) => {
                  //media item - image/video/3d ?
                  if (isPDPMediaItem(m)) {
                    return (
                      <div
                        key={`thumbnail-${m.id}`}
                        className={`${styles.thumbnail} ${state.selectedMediaIndex === index ? styles.selected : ''}`}
                        onClick={() => {
                          onThumbnailClick(index);
                          dispatch({ type: 'togglePCBBtnVisibility', isVisible: false });
                        }}>
                        {m.mediaType !== MediaType.Image && (
                          <div className={styles.badge}>
                            <Icon
                              name={m.mediaType === MediaType.YoutubeVideo ? 'play2' : m.mediaType === MediaType.View_3D ? '3d' : 'file'}
                              width={16}
                              height={16}></Icon>
                          </div>
                        )}
                        <img id={m.id} src={getThumbnail(m, props.media)} alt={`thumbnail-${m.name}`} />
                      </div>
                    );
                  }
                  // PCB ?
                  else if (state.isPCBAvailable)
                    return (
                      <div
                        key={`thumbnail-pcb`}
                        className={`${styles.thumbnail} ${state.selectedMediaIndex === index ? styles.selected : ''}`}
                        onClick={() => {
                          onThumbnailClick(index);
                          dispatch({ type: 'togglePCBBtnVisibility', isVisible: true });
                        }}>
                        <img src={pcb3d} alt={'thumbnail-pcb'} className={styles.pcb} />
                      </div>
                    );
                })}
            </div>

            <Icon
              name='chev-down'
              className={`
              ${styles.chevron} 
              ${styles.down} 
              ${!state.areDesktopChevronsVisible ? styles.hide : ''} 
              ${!state.isChevronDownEnabled ? styles.disabled : ''}`}
              onClick={() => onDesktopChevronClick(false)}></Icon>
          </div>
        </Responsive>
      </div>

      {/* Main container */}
      <div className={`${styles['main-container']} ${props.sticker ? styles['with-sticker'] : ''}`} {...handlers}>
        {/* Left chevron (tablet only) */}
        <Icon
          name='chev-left'
          className={`
        ${styles.chevron}
        ${styles.left}
        ${updatedMedia.media.length === 1 ? styles.hide : ''} 
        ${state.selectedMediaIndex === 0 ? styles.disabled : ''}`}
          onClick={() => onTabletChevronClick(false)}></Icon>

        <Responsive breakpoints={['desktop', 'lg-desktop']}>{renderMediaObject()}</Responsive>

        <Responsive breakpoints={['mobile', 'tablet']}>
          <div className={styles['media-wrapper']} ref={mediaWrapperRef}>
            {props.media.media.map((m) => {
              return renderMediaObject(m);
            })}
          </div>
        </Responsive>

        {props.sticker && (
          <Responsive breakpoints={['mobile', 'tablet', 'desktop', 'lg-desktop']}>
            <div className={styles['sticker']}>{<img src={props.sticker.content} alt={`sticker-${props.sticker.id}`}></img>}</div>
          </Responsive>
        )}

        {/* Right chevron (tablet only) */}
        <Icon
          name='chev-right'
          className={`
          ${styles.chevron}
        ${styles.right}
        ${updatedMedia.media.length === 1 ? styles.hide : ''} 
        ${state.selectedMediaIndex === props.media.media.length - 1 ? styles.disabled : ''}`}
          onClick={() => onTabletChevronClick(true)}></Icon>
      </div>

      {/* Position indicator */}
      <div className={styles['pi-wrapper']}>
        <Responsive breakpoints={['tablet', 'mobile']}>
          <PositionIndicator data={updatedMedia} selectedMediaIndex={state.selectedMediaIndex}></PositionIndicator>
        </Responsive>
      </div>

      {/* Media Modal (tablet & mobile) */}

      <div className={`${styles['modal-wrapper']} ${!state.isMediaModalOpen ? styles.hide : ''}`}>
        {state.isMediaModalOpen && (
          <MediaModal
            data={updatedMedia}
            labels={props.labels}
            currentMediaBrand={state.currentMediaBrand}
            currentMediaMPN={state.currentMediaMPN}
            selectedMediaIndex={state.selectedMediaIndex}
            isPCBAvailable={state.isPCBAvailable}
            onCloseCallback={toggleMediaModal}></MediaModal>
        )}
      </div>
    </div>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case 'resetState':
        const show = thumbnailsRef.current ? thumbnailsRef.current.scrollHeight > thumbnailsRef.current.clientHeight : true;

        //IE 11 fix
        if (thumbnailsRef.current) {
          thumbnailsRef.current.scrollTop = 0;
        }
        if (mediaWrapperRef.current) {
          mediaWrapperRef.current.scrollTop = 0;
        }

        return {
          ...initialState,
          showPCBBtn: isTabletDevice() ? state.showPCBBtn : initialState.showPCBBtn,
          selectedMediaIndex: isTabletDevice() ? state.selectedMediaIndex : initialState.selectedMediaIndex,
          areDesktopChevronsVisible: show,
          isMediaModalOpen: isDesktop() || isLargeDesktop() ? false : state.isMediaModalOpen, // close modal if resizing from m/t -> d/lg-d
        };
      case 'enableChevrons':
        return {
          ...state,
          isChevronUpEnabled: action.isChevronUpEnabled,
          isChevronDownEnabled: action.isChevronDownEnabled,
        };
      case 'selectThumbnail':
        return {
          ...state,
          selectedMediaIndex: action.index,
        };
      case 'toggleMediaModal':
        return {
          ...state,
          isMediaModalOpen: isMobileDevice() || isTabletDevice() ? !state.isMediaModalOpen : false,
          showPCBBtn: isPDPMediaItem(props.media.media[state.selectedMediaIndex]) ? state.showPCBBtn : state.isMediaModalOpen,
        };
      case 'setCurrentMedia': {
        return {
          ...state,
          currentMediaBrand: action.currentMediaBrand,
          currentMediaMPN: action.currentMediaMPN,
        };
      }
      case 'togglePCBBtnVisibility': {
        return {
          ...state,
          showPCBBtn: action.isVisible,
        };
      }
      case 'setPCBAvailibility': {
        return {
          ...state,
          isPCBAvailable: action.isAvailable,
        };
      }
    }
  }

  function resetState() {
    dispatch({
      type: 'resetState',
    });
  }

  function onDesktopChevronClick(isDirectionUp: boolean) {
    if (thumbnailsRef.current) {
      const multiplier = isDirectionUp ? -1 : 1;
      const imageheight = isLargeDesktop() ? 77 : 64; // height of image
      const margin = 22 + 4; //margin & allow for selected border
      const scrollHeight = multiplier * (imageheight + margin);

      //enable/disable up & down chevron
      dispatch({
        type: 'enableChevrons',
        isChevronUpEnabled: thumbnailsRef.current.scrollTop + scrollHeight > 0,
        isChevronDownEnabled:
          thumbnailsRef.current.offsetHeight + thumbnailsRef.current.scrollTop + scrollHeight < thumbnailsRef.current.scrollHeight,
      });

      // scroll images by 1
      thumbnailsRef.current.scrollBy({ behavior: 'smooth', top: scrollHeight });
    }
  }

  function onTabletChevronClick(isDirectionRight: boolean) {
    if (mediaWrapperRef.current) {
      const multiplier = isDirectionRight ? 1 : -1;
      const imageWidth = 328; // width of image container
      const scrollWidth = multiplier * imageWidth;
      const updatedIndex = state.selectedMediaIndex + multiplier;

      dispatch({
        type: 'selectThumbnail',
        index: updatedIndex,
      });

      dispatch({ type: 'togglePCBBtnVisibility', isVisible: !isPDPMediaItem(props.media.media[updatedIndex]) });

      // scroll images by 1
      mediaWrapperRef.current.scrollBy({ behavior: 'smooth', left: scrollWidth });
    }
  }

  function onMobileSwipe(isDirectionRight: boolean) {
    if (
      mediaWrapperRef.current &&
      (isMobileDevice() || isTabletDevice()) &&
      ((isDirectionRight && state.selectedMediaIndex !== 0) ||
        (!isDirectionRight && state.selectedMediaIndex !== updatedMedia.media.length - 1))
    ) {
      const multiplier = isDirectionRight ? -1 : 1;
      const imageWidth = isMobileDevice() ? 136 : 328; // width of unselected image
      const imageMargin = isMobileDevice() ? 24 : 0; // margin between images
      const scrollWidth = multiplier * (imageWidth + imageMargin);
      const updatedIndex = state.selectedMediaIndex + multiplier;

      dispatch({
        type: 'selectThumbnail',
        index: updatedIndex,
      });

      dispatch({ type: 'togglePCBBtnVisibility', isVisible: !isPDPMediaItem(props.media.media[updatedIndex]) });

      // scroll images by 1
      mediaWrapperRef.current.scrollBy({
        behavior: 'smooth',
        left: scrollWidth,
      });
    }
  }

  function onThumbnailClick(index: number) {
    dispatch({
      type: 'selectThumbnail',
      index: index,
    });
  }

  function renderMediaObject(media?: IPDPMediaItem | IPDPPCBDetails) {
    const currentMedia = media ? media : props.media.media[state.selectedMediaIndex];
    const selected = currentMedia === props.media.media[state.selectedMediaIndex];

    const toggleMediaModalIfSelected = (media: IPDPMediaItem | IPDPPCBDetails, isPCB?: boolean) => {
      if (selected && !isPCB) toggleMediaModal();
      else if (isMobileDevice()) {
        var index = props.media.media.findIndex((m) => m === media);
        onMobileSwipe(index < state.selectedMediaIndex);
      }
    };

    (!state.currentMediaBrand || !state.currentMediaMPN) &&
      !isPDPMediaItem(currentMedia) &&
      dispatch({ type: 'setCurrentMedia', currentMediaBrand: currentMedia.brand, currentMediaMPN: currentMedia.mpn });

    if (isPDPMediaItem(currentMedia)) {
      return (
        <div className={`${styles.media} ${selected ? styles.selected : ''}`} key={currentMedia?.id}>
          {/* VIDEO */}
          {currentMedia?.mediaType === MediaType.YoutubeVideo && (
            <iframe
              className={styles.video}
              title={currentMedia.id}
              src={currentMedia.url}
              allow='autoplay; encrypted-media'
              frameBorder='0'
              onClick={() => toggleMediaModalIfSelected(currentMedia)}></iframe>
          )}

          {/* IMG: tablet & mobile & (desktop if zoom not enabled) */}
          {currentMedia?.mediaType === MediaType.Image && (media || !props.media.isZoomEnabled) && (
            <img src={currentMedia.url} alt={currentMedia.name} onClick={() => toggleMediaModalIfSelected(currentMedia)}></img>
          )}
          {/* IMG: lg-desktop & desktop if zoom enabled */}
          {currentMedia?.mediaType === MediaType.Image && !media && props.media.isZoomEnabled && (
            <Magnifier
              src={currentMedia.url}
              zoomImgSrc={props.media.mediaUrl.replace(/\[width\]/g, '1200').replace('[name]', currentMedia.name)}></Magnifier>
          )}

          {/* 3D */}
          {currentMedia?.mediaType === MediaType.View_3D && (
            <>
              <iframe
                className={styles['three-d']}
                title={currentMedia.id}
                src={currentMedia.url}
                allow='autoplay; encrypted-media'
                frameBorder='0'
                scrolling='no'
                onClick={() => toggleMediaModalIfSelected(currentMedia)}></iframe>
            </>
          )}
          {props.media.disclaimerMessage.length > 0 && <div className={styles.disclaimer}>{props.media.disclaimerMessage}</div>}
        </div>
      );
    } else if (state.isPCBAvailable) {
      return (
        <div className={`${styles.media} ${currentMedia === props.media.media[state.selectedMediaIndex] ? styles.selected : ''}`} key='pcb'>
          <div className={styles.pcb} onClick={() => toggleMediaModalIfSelected(currentMedia, true)}>
            <img src={pcb3d} alt='pcb-3d' className={styles.logo}></img>

            {state.showPCBBtn && (
              <>
                <Button
                  className={styles['pcb-button-overlay']}
                  text={'View PCB Symbol, Footprint & 3D Model'}
                  buttonType='text'
                  onClick={(e: any) => {
                    const snapeda = document.getElementById('btn-popup-modal'); //snapeda button tag id
                    if (snapeda !== null) snapeda.click();
                  }}
                />
                {/*restrict size to pcb-button-overlay's size*/}
                <div className={styles['pcb-button-wrapper']}>
                  <div id='pcb-button' />
                </div>
              </>
            )}

            <img src={snapeda} alt='snapeda-logo' className={styles.logo}></img>
          </div>
        </div>
      );
    }
  }

  function toggleMediaModal() {
    dispatch({
      type: 'toggleMediaModal',
    });
  }
};

export default MediaCarousel;
